/**
 * QuestionBuilder — Phase B admin authoring UI.
 * Combines the main list, per-question editor (type-specific config), and
 * ground-truth editor. Emits an array of question objects matching Phase A's
 * `validate_and_normalize_questions` shape.
 */
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronUp, ChevronDown, X, AlertTriangle } from "lucide-react";
import { hasGroundTruth, validateQuestion } from "@/lib/questionValidation";

const TYPE_LABEL = {
  single_select_card: "Single choice (cards)",
  multi_select_card: "Multi choice (checkboxes)",
  short_text: "Short text",
  long_text: "Long text",
  likert_5: "Likert 1-5 scale",
  dropdown: "Dropdown",
  matrix_agree_disagree: "Agree/disagree matrix",
  conditional_single_select: "Single choice with follow-up text",
};
const DEFAULT_SCALE = ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"];

const blank = (type = "single_select_card") => ({
  id: `temp_${Math.random().toString(36).slice(2, 8)}`,
  type, text: "", required: true, weight: 1,
  ...(type === "matrix_agree_disagree" ? { matrix_statements: [""], matrix_scale: DEFAULT_SCALE } : {}),
  ...(["single_select_card","multi_select_card","dropdown","conditional_single_select"].includes(type) ? { options: ["", ""] } : {}),
  ...(type === "multi_select_card" ? { min_selections: 1, max_selections: 2 } : {}),
  ...(type === "long_text" ? { rows: 4, max_length: 4000 } : {}),
  ...(type === "short_text" ? { max_length: 500 } : {}),
  ...(type === "conditional_single_select" ? { conditional_option: "", conditional_placeholder: "Enter details..." } : {}),
  ground_truth: {},
});

export default function QuestionBuilder({ value, onChange }) {
  const questions = value || [];
  const patch = (idx, upd) => onChange(questions.map((q, i) => i === idx ? { ...q, ...upd } : q));
  const remove = (idx) => onChange(questions.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const add = () => onChange([...questions, blank()]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Questions</h4>
        <Button type="button" onClick={add} size="sm"
          className="bg-amber-700 hover:bg-amber-800" data-testid="qb-add">
          <Plus className="mr-1 h-3.5 w-3.5" /> Add question
        </Button>
      </div>
      {questions.length === 0 && (
        <p className="rounded-lg bg-neutral-50 px-4 py-3 text-sm text-neutral-500">
          No questions yet. Click &quot;Add question&quot; to build the task.
        </p>
      )}
      {questions.map((q, idx) => (
        <QuestionCard key={q.id} q={q} idx={idx} count={questions.length}
          onPatch={(u) => patch(idx, u)}
          onDelete={() => remove(idx)}
          onMove={(d) => move(idx, d)} />
      ))}
    </div>
  );
}

function QuestionCard({ q, idx, count, onPatch, onDelete, onMove }) {
  const [gtOpen, setGtOpen] = useState(false);
  const onType = (type) => onPatch({ ...blank(type), id: q.id, text: q.text, required: q.required, weight: q.weight });
  const err = validateQuestion(q, idx);
  const gtMissing = !hasGroundTruth(q);
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${err ? "border-red-300" : "border-neutral-200"}`}
      data-testid={`qb-card-${idx}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Question {idx + 1}</span>
          {err && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700"
              data-testid={`qb-error-${idx}`}>
              <AlertTriangle className="h-3 w-3" /> {err.replace(`Question ${idx + 1}: `, "")}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => onMove(-1)} disabled={idx === 0}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={idx === count - 1}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-100 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
          <button type="button" onClick={onDelete}
            className="rounded p-1 text-red-500 hover:bg-red-50" data-testid={`qb-delete-${idx}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Type</label>
          <Select value={q.type} onValueChange={onType}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(TYPE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Question text</label>
          <Textarea rows={2} value={q.text} onChange={(e) => onPatch({ text: e.target.value })}
            placeholder="Ask your question…" className="mt-1" data-testid={`qb-text-${idx}`} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={q.required} onChange={(e) => onPatch({ required: e.target.checked })} />
          Required
        </label>

        <TypeConfig q={q} onPatch={onPatch} />

        <button type="button" onClick={() => setGtOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-lg bg-neutral-50 px-3 py-2 text-xs font-medium text-neutral-700"
          data-testid={`qb-gt-toggle-${idx}`}>
          <span className="flex items-center gap-2">
            Ground truth (grading)
            {gtMissing && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
                title="No ground truth — this question won't be graded automatically"
                data-testid={`qb-gt-warn-${idx}`}>
                <AlertTriangle className="h-3 w-3" /> not graded
              </span>
            )}
          </span>
          {gtOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {gtOpen && (
          <div className="rounded-lg border border-neutral-100 p-3">
            <p className="mb-2 text-[11px] text-neutral-500">
              Used by AfriSelection AI to grade this question. Leave blank if subjective.
            </p>
            <GroundTruthEditor q={q} onPatch={(gt) => onPatch({ ground_truth: gt })} />
          </div>
        )}
      </div>
    </div>
  );
}

function ListEditor({ items, onChange, placeholder, testid, addLabel = "Add option" }) {
  return (
    <div className="space-y-1.5">
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-1.5">
          <Input value={it} placeholder={placeholder}
            onChange={(e) => onChange(items.map((x, j) => j === i ? e.target.value : x))}
            data-testid={`${testid}-${i}`} />
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="rounded p-2 text-neutral-400 hover:bg-red-50 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...(items || []), ""])}
        className="text-xs font-medium text-amber-700 hover:underline">+ {addLabel}</button>
    </div>
  );
}

function TypeConfig({ q, onPatch }) {
  const t = q.type;
  if (t === "single_select_card" || t === "dropdown") {
    return (
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Options</label>
        <ListEditor items={q.options} onChange={(v) => onPatch({ options: v })} placeholder="Option text" testid={`qb-opt-${q.id}`} />
      </div>
    );
  }
  if (t === "multi_select_card") {
    return (
      <>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Options</label>
          <ListEditor items={q.options} onChange={(v) => onPatch({ options: v })} placeholder="Option text" testid={`qb-opt-${q.id}`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase text-neutral-500">Min selections</label>
            <Input type="number" min={0} value={q.min_selections || 0}
              onChange={(e) => onPatch({ min_selections: parseInt(e.target.value) || 0 })} />
          </div>
          <div>
            <label className="text-[10px] uppercase text-neutral-500">Max selections</label>
            <Input type="number" min={1} value={q.max_selections || 1}
              onChange={(e) => onPatch({ max_selections: parseInt(e.target.value) || 1 })} />
          </div>
        </div>
      </>
    );
  }
  if (t === "short_text" || t === "long_text") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Min length</label>
          <Input type="number" min={0} value={q.min_length || 0}
            onChange={(e) => onPatch({ min_length: parseInt(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Max length</label>
          <Input type="number" min={0} value={q.max_length || 0}
            onChange={(e) => onPatch({ max_length: parseInt(e.target.value) || 0 })} />
        </div>
        {t === "long_text" && (
          <div>
            <label className="text-[10px] uppercase text-neutral-500">Rows</label>
            <Input type="number" min={2} value={q.rows || 4}
              onChange={(e) => onPatch({ rows: parseInt(e.target.value) || 4 })} />
          </div>
        )}
      </div>
    );
  }
  if (t === "likert_5") {
    const el = q.endpoint_labels || {};
    return (
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Low label</label>
          <Input value={el.low || ""} placeholder="Strongly disagree"
            onChange={(e) => onPatch({ endpoint_labels: { ...el, low: e.target.value } })} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-neutral-500">High label</label>
          <Input value={el.high || ""} placeholder="Strongly agree"
            onChange={(e) => onPatch({ endpoint_labels: { ...el, high: e.target.value } })} />
        </div>
      </div>
    );
  }
  if (t === "matrix_agree_disagree") {
    return (
      <>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Statements</label>
          <ListEditor items={q.matrix_statements} onChange={(v) => onPatch({ matrix_statements: v })}
            placeholder="e.g. React is fun" addLabel="Add statement" testid={`qb-stmt-${q.id}`} />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Scale labels</label>
          <ListEditor items={q.matrix_scale} onChange={(v) => onPatch({ matrix_scale: v })}
            placeholder="Scale label" addLabel="Add scale point" testid={`qb-scale-${q.id}`} />
        </div>
      </>
    );
  }
  if (t === "conditional_single_select") {
    return (
      <>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">Options</label>
          <ListEditor items={q.options} onChange={(v) => onPatch({ options: v })} placeholder="Option text" testid={`qb-opt-${q.id}`} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Follow-up triggered by</label>
          <Select value={q.conditional_option || ""} onValueChange={(v) => onPatch({ conditional_option: v })}>
            <SelectTrigger className="mt-1"><SelectValue placeholder="Pick option" /></SelectTrigger>
            <SelectContent>{(q.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Follow-up placeholder</label>
          <Input value={q.conditional_placeholder || ""}
            onChange={(e) => onPatch({ conditional_placeholder: e.target.value })} />
        </div>
      </>
    );
  }
  return null;
}

function GroundTruthEditor({ q, onPatch }) {
  const gt = q.ground_truth || {};
  const t = q.type;
  if (t === "single_select_card" || t === "dropdown" || t === "conditional_single_select") {
    const alts = gt.correct_options || (gt.correct_option ? [gt.correct_option] : []);
    return (
      <div className="space-y-2">
        <Select value={alts[0] || ""} onValueChange={(v) => onPatch({ ...gt, correct_option: v, correct_options: [v, ...alts.slice(1)].filter(Boolean) })}>
          <SelectTrigger><SelectValue placeholder="Correct option" /></SelectTrigger>
          <SelectContent>{(q.options || []).filter(Boolean).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
        {t === "conditional_single_select" && (
          <div>
            <label className="text-[10px] uppercase text-neutral-500">Expected keywords in follow-up (comma-separated)</label>
            <Input value={(gt.text_keywords || []).join(", ")}
              onChange={(e) => onPatch({ ...gt, text_keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
          </div>
        )}
      </div>
    );
  }
  if (t === "multi_select_card") {
    const set = new Set(gt.correct_set || []);
    return (
      <div className="space-y-1">
        {(q.options || []).filter(Boolean).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={set.has(o)} onChange={(e) => {
              const next = new Set(set); if (e.target.checked) next.add(o); else next.delete(o);
              onPatch({ ...gt, correct_set: [...next] });
            }} /> {o}
          </label>
        ))}
      </div>
    );
  }
  if (t === "short_text" || t === "long_text") {
    return (
      <div className="space-y-2">
        <Textarea rows={2} placeholder="Reference answer"
          value={gt.reference_answer || ""} onChange={(e) => onPatch({ ...gt, reference_answer: e.target.value })} />
        <Input placeholder="Keywords (comma-separated)"
          value={(gt.keywords || []).join(", ")}
          onChange={(e) => onPatch({ ...gt, keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
      </div>
    );
  }
  if (t === "likert_5") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Expected value</label>
          <Input type="number" min={1} max={5} value={gt.expected || ""}
            onChange={(e) => onPatch({ ...gt, expected: parseInt(e.target.value) || 0 })} />
        </div>
        <div>
          <label className="text-[10px] uppercase text-neutral-500">Tolerance</label>
          <Input type="number" min={0} max={4} value={gt.tolerance ?? 1}
            onChange={(e) => onPatch({ ...gt, tolerance: parseInt(e.target.value) || 0 })} />
        </div>
      </div>
    );
  }
  if (t === "matrix_agree_disagree") {
    const exp = gt.expected || {};
    const scale = q.matrix_scale || DEFAULT_SCALE;
    return (
      <div className="space-y-2">
        {(q.matrix_statements || []).filter(Boolean).map((stmt) => (
          <div key={stmt} className="flex items-center gap-2">
            <span className="flex-1 text-xs text-neutral-700">{stmt}</span>
            <Select value={String(exp[stmt] || "")}
              onValueChange={(v) => onPatch({ ...gt, expected: { ...exp, [stmt]: parseInt(v) } })}>
              <SelectTrigger className="w-40"><SelectValue placeholder="Expected" /></SelectTrigger>
              <SelectContent>{scale.map((label, i) => <SelectItem key={label} value={String(i + 1)}>{label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    );
  }
  return null;
}
